import { Calculator } from '@kmx/online-finance-helpers';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import { formatCurrency } from '@/helpers/formatters';
import type { FbsApplication } from '@/types/FbsApplication';
import type { FbsDecision } from '@/types/FbsDecision';
import type { FeeRange } from '@/types/FeeRange';

type PriceTableProps = {
  basePrice: number;
  downPayment: number;
  feesList: FeeRange[];
  fbsDecision: FbsDecision;
  fbsApplication: FbsApplication;
};

const PriceTable = ({
  feesList,
  basePrice,
  downPayment,
  fbsDecision,
  fbsApplication,
}: PriceTableProps) => {
  const { tradeInBreakdown } = fbsApplication;
  const { conditionAmountRounded, taxesAndFeesRounded } = fbsDecision;

  const estimatedFees =
    taxesAndFeesRounded ||
    Calculator.lookupTaxesAndFeesByVehiclePrice(feesList, basePrice);

  const formattedFees = formatCurrency(estimatedFees);

  const formattedVehiclePrice = useMemo(() => {
    const price = basePrice;
    return `${price ? formatCurrency(price) : 'N/A'}`;
  }, [basePrice]);

  const formattedDownpayment = formatCurrency(
    downPayment + conditionAmountRounded,
  );

  const showTradeInEquity = useMemo(() => {
    if (!tradeInBreakdown) return false;
    return !Object.values(tradeInBreakdown).every((x) => x === null || x === 0);
  }, [tradeInBreakdown]);

  const formattedFinalPrice = useMemo(() => {
    if (!showTradeInEquity || !tradeInBreakdown) {
      return formatCurrency(
        basePrice + estimatedFees - (downPayment + conditionAmountRounded),
      );
    }

    return formatCurrency(
      basePrice +
        estimatedFees -
        (downPayment + conditionAmountRounded) -
        (tradeInBreakdown.negativeEquity +
          tradeInBreakdown.appliedPositiveEquity),
    );
  }, [
    basePrice,
    downPayment,
    estimatedFees,
    tradeInBreakdown,
    showTradeInEquity,
    conditionAmountRounded,
  ]);

  /*
    if negativeEquity is nonzero, show negativeEquity
    if positiveEquity is >=0, show appliedPositiveEquity (i.e its possible to get a positive equity of $0)
  */
  const formattedTradeInEquity = useMemo(() => {
    if (!tradeInBreakdown) return null;

    const { appliedPositiveEquity, negativeEquity } = tradeInBreakdown;
    if (negativeEquity !== 0)
      return `${formatCurrency(Math.abs(negativeEquity))}`;

    return `${formatCurrency(-appliedPositiveEquity)}`;
  }, [tradeInBreakdown]);

  return (
    <>
      <Typography variant="h6"> Cost Breakdown </Typography>

      <Table size="small" className="fbs-terms-price-table">
        <TableBody>
          <TableRow>
            <TableCell>Vehicle price</TableCell>
            <TableCell
              align="right"
              aria-describedby="vehicle-price-disclaimer"
            >
              {formattedVehiclePrice}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>Est. tax, title &#38; fees</TableCell>
            <TableCell align="right">{formattedFees}</TableCell>
          </TableRow>

          {showTradeInEquity && (
            <TableRow>
              <TableCell>Trade-in equity</TableCell>
              <TableCell align="right">{formattedTradeInEquity}</TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell>Down payment</TableCell>
            <TableCell align="right">- {formattedDownpayment}</TableCell>
          </TableRow>

          <TableRow className="price-table-final">
            <TableCell>
              <Typography variant="subtitle1" component="span">
                Total finance amount
              </Typography>
            </TableCell>

            <TableCell align="right">
              <Typography variant="subtitle1" component="span">
                {formattedFinalPrice}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default PriceTable;
